import { CircularProgress } from "@mui/material";
import { Button } from "@mui/material";
import { Paper } from "@mui/material";
import { Typography } from "@mui/material";
import { Box } from "@mui/material";
import { useRef } from "react";
import { ApiSuccessResponse } from "../../../types/api";
import { useState } from "react";
import { useApi } from "../../../services/useApi";
import axios from "axios";
import { authService } from "../../../services/auth.service";

interface Props {
  onLoadData: (data: any[][], columns: any[]) => void;
  onLoading?: (loading: boolean) => void;
  onError?: (error: string) => void;
}

const UploadData = ({ onLoadData, onLoading, onError }: Props) => {
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleLoadData = async (_file: File) => {
    onLoading(true);
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", _file);

      // Create axios instance with longer timeout for large file uploads
      const uploadApi = axios.create({
        baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000/api',
        timeout: 600000, // 10 minutes timeout for large files
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Add auth token if available
      const token = authService.getToken();
      if (token) {
        uploadApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      const response = await uploadApi.post<ApiSuccessResponse<any[][]>>("/main/load-file", formData);

      if (response.data.success) {
        const responseData = response.data.data;

        if (!Array.isArray(responseData) || responseData.length === 0) {
          onError("No data received from server");
          return;
        }

        // Generate column headers based on array indices
        const columns = responseData[0].map((_, index) => ({
          id: `col${index}`,
          label: `Column ${index + 1}`,
        }));

        onLoadData(responseData, columns);
      }
    } catch (error) {
      onError("Error uploading file");
    } finally {
      setLoading(false);
      onLoading(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
          Source file
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Large files may take up to 10 minutes to upload. Please be patient.
        </Typography>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <input
          type="file"
          accept=".csv,.xlsx,.xls,.txt"
          onChange={(e) => {
            handleFileChange(e);
            if (e.target.files && e.target.files[0]) {
              handleLoadData(e.target.files[0]);
            }
          }}
          style={{ display: "none" }}
          ref={fileInputRef}
        />
        <Button
          variant="outlined"
          onClick={() => fileInputRef.current?.click()}
          sx={{ minWidth: 120 }}
          disabled={loading}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Uploading...
            </>
          ) : (
            "Select File"
          )}
        </Button>
        {file && (
          <Typography variant="body2" sx={{ ml: 2 }}>
            Selected file: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default UploadData;
